/**
 * Lead Management Types
 *
 * Shared TypeScript interfaces for the Lead Management System.
 * Used by both builder (dashboard) for CRM functionality.
 */

// Lead status progression
export type LeadStatus = 'open' | 'won' | 'lost' | 'archived'

// Lead source options
export type LeadSource =
  | 'website'
  | 'phone'
  | 'email'
  | 'referral'
  | 'walk_in'
  | 'advertising'
  | 'other'

// Activity types
export type LeadActivityType =
  | 'note'
  | 'call'
  | 'email'
  | 'meeting'
  | 'stage_change'
  | 'status_change'
  | 'assignment'
  | 'follow_up'
  | 'quote_updated'
  | 'system'

// Follow-up status
export type FollowUpStatus = 'pending' | 'completed' | 'cancelled'

/**
 * Lead stage in the pipeline
 */
export interface LeadStage {
  id: string
  accountId: string
  name: string
  color: string
  position: number
  isDefault: boolean
  isWon: boolean
  isLost: boolean
  createdAt: string
  updatedAt: string
}

/**
 * Score breakdown details
 */
export interface LeadScoreBreakdown {
  has_email?: number
  has_phone?: number
  high_value?: number
  medium_value?: number
  vehicle_selected?: number
  urgent_booking?: number
  has_date?: number
  referral_source?: number
  website_source?: number
  value_score?: number
}

/**
 * Core lead entity
 */
export interface Lead {
  id: string
  accountId: string
  quoteId?: string
  stageId: string
  source: LeadSource
  sourceDetail?: string
  customerName: string
  customerEmail?: string
  customerPhone?: string
  companyName?: string
  serviceType?: 'point_to_point' | 'hourly'
  pickupAddress?: string
  pickupDatetime?: string
  dropoffAddress?: string
  estimatedValue?: number
  score: number
  scoreBreakdown?: LeadScoreBreakdown
  assignedTo?: string
  assignedAt?: string
  status: LeadStatus
  lostReason?: string
  outcomeNotes?: string // Optional notes when marking won/lost
  nextFollowUpAt?: string
  lastContactedAt?: string
  firstContactedAt?: string // For speed-to-lead metrics
  contactCount: number
  bookingId?: string
  convertedAt?: string
  tags: string[]
  notes?: string
  createdAt: string
  updatedAt: string
}

/**
 * Lead with related data for list display
 */
export interface LeadWithRelations extends Lead {
  stage?: LeadStage
  assignee?: {
    id: string
    fullName?: string
    email: string
  }
  account?: {
    id: string
    name: string
    slug: string
  }
}

/**
 * Lead activity entry
 */
export interface LeadActivity {
  id: string
  leadId: string
  activityType: LeadActivityType
  title: string
  description?: string
  oldValue?: string
  newValue?: string
  performedBy?: string
  performer?: {
    id: string
    fullName?: string
    email: string
  }
  createdAt: string
}

/**
 * Lead follow-up task
 */
export interface LeadFollowUp {
  id: string
  leadId: string
  title: string
  description?: string
  dueAt: string
  reminderAt?: string
  reminderSent: boolean
  assignedTo: string
  assignee?: {
    id: string
    fullName?: string
    email: string
  }
  status: FollowUpStatus
  completedAt?: string
  completedBy?: string
  createdAt: string
  updatedAt: string
}

/**
 * Create lead request
 */
export interface CreateLeadRequest {
  source: LeadSource
  sourceDetail?: string
  customerName: string
  customerEmail?: string
  customerPhone?: string
  companyName?: string
  serviceType?: 'point_to_point' | 'hourly'
  pickupAddress?: string
  pickupDatetime?: string
  dropoffAddress?: string
  estimatedValue?: number
  notes?: string
  tags?: string[]
}

/**
 * Update lead request
 */
export interface UpdateLeadRequest {
  stageId?: string
  source?: LeadSource
  sourceDetail?: string
  customerName?: string
  customerEmail?: string
  customerPhone?: string
  companyName?: string
  serviceType?: 'point_to_point' | 'hourly'
  pickupAddress?: string
  pickupDatetime?: string
  dropoffAddress?: string
  estimatedValue?: number
  assignedTo?: string | null
  status?: LeadStatus
  lostReason?: string
  outcomeNotes?: string
  notes?: string
  tags?: string[]
}

/**
 * Create activity request
 */
export interface CreateActivityRequest {
  leadId: string
  activityType: 'note' | 'call' | 'email' | 'meeting'
  title: string
  description?: string
}

/**
 * Create follow-up request
 */
export interface CreateFollowUpRequest {
  leadId: string
  title: string
  description?: string
  dueAt: string
  reminderAt?: string
  assignedTo?: string
}

/**
 * Update follow-up request
 */
export interface UpdateFollowUpRequest {
  title?: string
  description?: string
  dueAt?: string
  reminderAt?: string
  assignedTo?: string
  status?: FollowUpStatus
}

/**
 * Lead filters for list view
 */
export interface LeadFilters {
  status?: LeadStatus[]
  stageIds?: string[]
  sources?: LeadSource[]
  assignedTo?: string | 'unassigned'
  scoreMin?: number
  scoreMax?: number
  createdAfter?: string
  createdBefore?: string
  search?: string
  tags?: string[]
}

/**
 * Lead sort options
 */
export type LeadSortField =
  | 'created_at'
  | 'updated_at'
  | 'score'
  | 'estimated_value'
  | 'customer_name'
  | 'next_follow_up_at'

export type SortDirection = 'asc' | 'desc'

export interface LeadSort {
  field: LeadSortField
  direction: SortDirection
}

/**
 * Lead list response with pagination
 */
export interface LeadListResponse {
  leads: LeadWithRelations[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

/**
 * Lead statistics for dashboard
 */
export interface LeadStats {
  total: number
  open: number
  won: number
  lost: number
  archived: number
  thisWeek: number
  conversionRate: number // (won / (won + lost)) * 100
  averageScore: number
  followUpsDue: number
  byStage: {
    stageId: string
    stageName: string
    stageColor: string
    count: number
  }[]
  bySource: {
    source: LeadSource
    count: number
  }[]
}

/**
 * Kanban board data
 */
export interface LeadKanbanData {
  stages: LeadStage[]
  leadsByStage: Record<string, Lead[]>
  total: number
}

// Status display helpers
export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  open: 'Open',
  won: 'Won',
  lost: 'Lost',
  archived: 'Archived',
}

export const LEAD_STATUS_COLORS: Record<
  LeadStatus,
  { bg: string; text: string; border: string }
> = {
  open: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
  },
  won: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
  },
  lost: {
    bg: 'bg-red-50',
    text: 'text-red-500',
    border: 'border-red-200',
  },
  archived: {
    bg: 'bg-gray-50',
    text: 'text-gray-500',
    border: 'border-gray-200',
  },
}

export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  website: 'Website',
  phone: 'Phone',
  email: 'Email',
  referral: 'Referral',
  walk_in: 'Walk-in',
  advertising: 'Advertising',
  other: 'Other',
}

export const LEAD_SOURCE_ICONS: Record<LeadSource, string> = {
  website: 'Globe',
  phone: 'Phone',
  email: 'Mail',
  referral: 'Users',
  walk_in: 'MapPin',
  advertising: 'Megaphone',
  other: 'MoreHorizontal',
}

export const ACTIVITY_TYPE_LABELS: Record<LeadActivityType, string> = {
  note: 'Note',
  call: 'Phone Call',
  email: 'Email',
  meeting: 'Meeting',
  stage_change: 'Stage Change',
  status_change: 'Status Change',
  assignment: 'Assignment',
  follow_up: 'Follow-up',
  quote_updated: 'Quote Updated',
  system: 'System',
}

export const ACTIVITY_TYPE_ICONS: Record<LeadActivityType, string> = {
  note: 'StickyNote',
  call: 'Phone',
  email: 'Mail',
  meeting: 'Calendar',
  stage_change: 'ArrowRight',
  status_change: 'CheckCircle',
  assignment: 'UserPlus',
  follow_up: 'Clock',
  quote_updated: 'FileText',
  system: 'Cog',
}

/**
 * Score level configuration
 */
export interface ScoreLevel {
  label: string
  minScore: number
  maxScore: number
  color: string
  bgColor: string
}

export const SCORE_LEVELS: ScoreLevel[] = [
  { label: 'Hot', minScore: 70, maxScore: 100, color: 'text-red-600', bgColor: 'bg-red-100' },
  { label: 'Warm', minScore: 40, maxScore: 69, color: 'text-orange-600', bgColor: 'bg-orange-100' },
  { label: 'Cold', minScore: 0, maxScore: 39, color: 'text-blue-600', bgColor: 'bg-blue-100' },
]

/**
 * Get score level for a given score
 */
export function getScoreLevel(score: number): ScoreLevel {
  return (
    SCORE_LEVELS.find(
      (level) => score >= level.minScore && score <= level.maxScore
    ) || SCORE_LEVELS[SCORE_LEVELS.length - 1]
  )
}

/**
 * Default stages for new accounts
 */
export const DEFAULT_LEAD_STAGES = [
  { name: 'New Lead', color: '#6366f1', position: 0, isDefault: true, isWon: false, isLost: false },
  { name: 'Contacted', color: '#8b5cf6', position: 1, isDefault: false, isWon: false, isLost: false },
  { name: 'Qualified', color: '#ec4899', position: 2, isDefault: false, isWon: false, isLost: false },
  { name: 'Proposal Sent', color: '#f59e0b', position: 3, isDefault: false, isWon: false, isLost: false },
  { name: 'Won', color: '#10b981', position: 4, isDefault: false, isWon: true, isLost: false },
  { name: 'Lost', color: '#6b7280', position: 5, isDefault: false, isWon: false, isLost: true },
]

/**
 * Common lost reasons
 */
export const LOST_REASONS = [
  'Price too high',
  'Booked with competitor',
  'Changed plans',
  'No response',
  'Wrong service type',
  'Date not available',
  'Other',
]

/**
 * Lead outcome type (for won/lost dialogs)
 */
export type LeadOutcome = 'won' | 'lost'

/**
 * Lead needing attention (for alerts)
 */
export interface LeadNeedingAttention {
  id: string
  customerName: string
  customerEmail?: string
  customerPhone?: string
  createdAt: string
  score: number
  reason: 'awaiting_contact' | 'overdue_followup'
  minutesWaiting?: number
  followUpTitle?: string
  followUpDueAt?: string
}

/**
 * Speed to lead metrics
 */
export interface SpeedToLeadMetrics {
  avgMinutes: number | null
  medianMinutes: number | null
  neverContacted: number
  contactedToday: number
  distribution: {
    range: string
    count: number
    percentage: number
  }[]
}

/**
 * Needs attention data
 */
export interface LeadsNeedingAttentionData {
  awaitingContact: LeadNeedingAttention[]
  overdueFollowUps: LeadNeedingAttention[]
  totalCount: number
}

/**
 * Enhanced lead stats for sales focus
 */
export interface EnhancedLeadStats extends LeadStats {
  newToday: number
  awaitingContact: number
  hotLeads: number
  avgResponseMinutes: number | null
}

/**
 * Simplified stages for new accounts (as of 2026-02-04)
 */
export const SIMPLIFIED_LEAD_STAGES = [
  { name: 'New Lead', color: '#3b82f6', position: 0, isDefault: true, isWon: false, isLost: false },
  { name: 'Contacted', color: '#8b5cf6', position: 1, isDefault: false, isWon: false, isLost: false },
  { name: 'Follow Up', color: '#f59e0b', position: 2, isDefault: false, isWon: false, isLost: false },
  { name: 'Won', color: '#10b981', position: 3, isDefault: false, isWon: true, isLost: false },
  { name: 'Lost', color: '#6b7280', position: 4, isDefault: false, isWon: false, isLost: true },
]
